import {
  APInvoiceStatus,
  MatchConfidenceStatus,
  type TroubleData,
} from '@erp/shared-types';
import type { EntityAssociationMatchMetadata } from '@erp/rpc';
import type {
  APInvoiceHold,
  PurchaseOrderLineItem,
  SalesOrderLineItem,
  Charge,
} from '@erp/rpc';
import type { OnHoldSummary } from '@/components/common/TroubleDataBadges';
import { hasActiveTroubleValue } from '@/components/common/troubleEntries';
import type { HoldOrigin } from '@/components/common/holdSources';

/** Must match backend HIGH_CONFIDENCE_THRESHOLD in accounts-payable/constants.ts */
const HIGH_CONFIDENCE_THRESHOLD = 0.95;

export type MatchDisplayState =
  'DRAFT' | 'MATCHED' | 'ACCEPTED' | 'FINALIZED' | 'VOID' | 'IGNORED';

/**
 * Badge text keyed by display state. Title Case, like every other badge it
 * sits beside (payment status, trouble flags), so the header reads as one
 * row of labels rather than one shouting one.
 */
export const DISPLAY_STATE_LABELS: Record<MatchDisplayState, string> = {
  DRAFT: 'Draft',
  MATCHED: 'Matched',
  ACCEPTED: 'Accepted',
  FINALIZED: 'Finalized',
  VOID: 'Void',
  IGNORED: 'Ignored',
};

interface MatchSummary {
  confirmed: number;
  tentative: number;
  unmatched: number;
  total: number;
}

/**
 * Map the invoice's DB status onto the user-facing display state.
 *
 * The status IS the display state. `checkAndUpdateInvoiceMatchStatus` keeps
 * DRAFT ↔ MATCHED in step with the matches on every match edit, so the only
 * time an invoice is DRAFT with every line confirmed is right after a revert
 * from accepted — and that is precisely when it must read "Draft", not
 * "Matched" (the badge used to re-derive MATCHED from the summary here, which
 * silently undid the revert in the UI). `summary` is accepted for call-site
 * compatibility; it no longer influences the result.
 */
export function getMatchDisplayState({
  status,
}: {
  status: string;
  summary?: MatchSummary | null | undefined;
}): MatchDisplayState {
  if (status === APInvoiceStatus.MATCHED) return 'MATCHED';
  if (status === APInvoiceStatus.ACCEPTED) return 'ACCEPTED';
  if (status === APInvoiceStatus.FINALIZED) return 'FINALIZED';
  if (status === APInvoiceStatus.VOID) return 'VOID';
  if (status === APInvoiceStatus.IGNORED) return 'IGNORED';
  return 'DRAFT';
}

/**
 * Classify a match as tentative based on metadata.
 * Mirrors backend classifyMatchStatus logic — checks locked-in matchStatus
 * first, falls back to confidence threshold for legacy records.
 */
export function isTentativeMatch(
  metadata: EntityAssociationMatchMetadata | null | undefined,
): boolean {
  if (!metadata) return false;
  if (metadata.matchStatus === MatchConfidenceStatus.CONFIRMED) return false;
  if (metadata.matchStatus === MatchConfidenceStatus.TENTATIVE) return true;
  // Legacy fallback: no matchStatus, derive from confidence
  if (metadata.confidence === undefined) return false;
  return metadata.confidence < HIGH_CONFIDENCE_THRESHOLD;
}

/**
 * Whether a match was created manually by a user (vs. produced automatically
 * by reconciliation or the AI agent). Mirrors the backend convention in
 * `classifyMatchStatus`: automatic matches always persist metadata
 * (confidence / matchMethod / matchStatus), while a manual match carries none
 * (except a manual aggregation, which is recorded as `manual_aggregation`).
 */
export function isManualMatch(
  metadata: EntityAssociationMatchMetadata | null | undefined,
): boolean {
  return (
    metadata == null ||
    Object.keys(metadata).length === 0 ||
    // A user-made aggregation carries group metadata but is still a person's
    // choice, not the matcher's.
    metadata.matchMethod === 'manual_aggregation'
  );
}

export function getActiveHoldPay(
  matchedPOLine?: PurchaseOrderLineItem | null,
  matchedSOLine?: SalesOrderLineItem | null,
  matchedCharge?: Charge | null,
): boolean {
  return (
    !!matchedPOLine?.paymentHold ||
    hasActiveTroubleValue(matchedPOLine?.troubleData, 'HOLD_PAY') ||
    hasActiveTroubleValue(matchedSOLine?.troubleData, 'HOLD_PAY') ||
    hasActiveTroubleValue(matchedCharge?.troubleData, 'HOLD_PAY')
  );
}

export function getActiveConsign(
  matchedSOLine?: SalesOrderLineItem | null,
): boolean {
  return (
    !!matchedSOLine?.isConsignment ||
    hasActiveTroubleValue(matchedSOLine?.troubleData, 'CONSIGN')
  );
}

/** Famous load charges are tagged with their source table at ingest. */
export function chargeHoldOrigin(charge: Charge): HoldOrigin {
  return charge.originMetadata?.originEntity === 'AR_LOAD_CHARGE'
    ? 'SALES_LOAD_CHARGE'
    : 'CHARGE';
}

/**
 * The trouble entries shown on a match chip and the record they came from.
 * A line matches one target, so the first matched record wins.
 */
export function getMatchTrouble(
  matchedPOLine?: PurchaseOrderLineItem | null,
  matchedSOLine?: SalesOrderLineItem | null,
  matchedCharge?: Charge | null,
): { troubleData: TroubleData; origin: HoldOrigin | null } {
  if (matchedPOLine?.troubleData) {
    return {
      troubleData: matchedPOLine.troubleData,
      origin: 'PURCHASE_ORDER_LINE',
    };
  }
  if (matchedSOLine?.troubleData) {
    return {
      troubleData: matchedSOLine.troubleData,
      origin: 'SALES_ORDER_LINE',
    };
  }
  if (matchedCharge?.troubleData) {
    return {
      troubleData: matchedCharge.troubleData,
      origin: chargeHoldOrigin(matchedCharge),
    };
  }
  return { troubleData: null, origin: null };
}

/**
 * Holds that block paying the record. A load hold is shown but does not block
 * payment (the backend's hold-pay check ignores it too).
 */
const PAYMENT_BLOCKING_HOLDS = new Set(['HOLD_PAY', 'CONSIGN']);

function holdReference(hold: APInvoiceHold): string | undefined {
  if (!hold.documentNumber) return undefined;
  return hold.lineNumber != null
    ? `${hold.documentNumber} #${hold.lineNumber}`
    : hold.documentNumber;
}

/**
 * Invoice-level hold summary from the holds on its linked documents.
 * `onHoldCount` counts records with at least one payment-blocking hold.
 */
export function summarizeInvoiceHolds(
  holds: readonly APInvoiceHold[],
): OnHoldSummary {
  const blockedRecords = new Set<string>();
  for (const hold of holds) {
    if (PAYMENT_BLOCKING_HOLDS.has(hold.value))
      blockedRecords.add(hold.recordId);
  }
  return {
    onHoldCount: blockedRecords.size,
    holdSources: holds.map((hold) => ({
      value: hold.value,
      origin: hold.origin,
      reference: holdReference(hold),
    })),
  };
}

export type AcceptWarning =
  'PAYMENT_HOLD' | 'MATCHED_HOLD_PAY' | 'HOLDS_LOADING' | 'REVIEW_HOLDS';

/**
 * Warnings for the Accept Matches confirmation, in display order. Holds are
 * soft: they warn, never block (the source ERP is not in live sync).
 * An invoice that is not fully matched never reaches this dialog.
 */
export function acceptWarnings(state: {
  onPaymentHold: boolean;
  matchedOnHoldPay: boolean;
  holdsLoading: boolean;
  holdsFailed: boolean;
  onHoldCount: number;
}): AcceptWarning[] {
  const holdListWarning: AcceptWarning[] = state.holdsLoading
    ? ['HOLDS_LOADING']
    : state.onHoldCount > 0 || state.holdsFailed
      ? ['REVIEW_HOLDS']
      : [];
  return [
    ...(state.onPaymentHold ? (['PAYMENT_HOLD'] as const) : []),
    ...(state.matchedOnHoldPay ? (['MATCHED_HOLD_PAY'] as const) : []),
    ...holdListWarning,
  ];
}

/** Badge color classes keyed by display state. */
export const DISPLAY_STATE_BADGE_CLASSES: Record<MatchDisplayState, string> = {
  DRAFT: 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200',
  MATCHED: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
  ACCEPTED: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
  FINALIZED: 'bg-gray-900 text-white hover:bg-gray-800',
  VOID: 'bg-gray-100 text-gray-600 border-gray-200',
  IGNORED: 'bg-gray-100 text-gray-600 border-gray-200',
};
